import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Trash2, PencilIcon, UserPlus } from 'lucide-react';

// Memoized FormContent to avoid unnecessary re-renders and preserve focus.
const FormContent = memo(({
  isEdit,
  formData,
  errorMessage,
  handleInputChange,
  handleCouponCodeChange,
  addCouponCode,
  removeCouponCode,
}) => {
  return (
    <div className="space-y-4 py-4">
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">
          {errorMessage}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="username">Username *</Label>
        <Input
          id="username"
          name="username"
          placeholder="Enter username"
          value={formData.username}
          onChange={handleInputChange}
          disabled={isEdit}
          required
        />
        {isEdit && (
          <p className="text-xs text-muted-foreground">Username cannot be changed</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="fullName">Full Name *</Label>
        <Input
          id="fullName"
          name="fullName"
          placeholder="Enter full name"
          value={formData.fullName}
          onChange={handleInputChange}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email (Optional)</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="Enter email"
          value={formData.email}
          onChange={handleInputChange}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone (Optional)</Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          placeholder="Enter phone number"
          value={formData.phone}
          onChange={handleInputChange}
        />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label>Coupon Codes *</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCouponCode}
            className="h-8"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Code
          </Button>
        </div>
        <div className="space-y-2">
          {formData.couponCodes.map((code, index) => (
            <div key={`coupon-${index}`} className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => handleCouponCodeChange(index, e.target.value)}
                placeholder={index === 0 ? "Primary coupon code" : "Additional coupon code"}
                required
              />
              {formData.couponCodes.length > 1 && (
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={() => removeCouponCode(index)}
                  className="h-10 w-10"
                >
                  <Minus className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {formData.couponCodes.length > 0 && (
          <p className="text-xs text-muted-foreground">First code is the primary coupon code</p>
        )}
      </div>

      <p className="text-sm text-muted-foreground">* Required fields</p>
    </div>
  );
});

const ManageAgents = () => {
  const [agents, setAgents] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    couponCodes: [''],
    email: '',
    phone: ''
  });
  const [errorMessage, setErrorMessage] = useState('');

  const baseUrl = 'https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod';

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${baseUrl}/agents/agents`);
      if (!response.ok) {
        throw new Error('Failed to fetch agents');
      }
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      fullName: '',
      couponCodes: [''],
      email: '',
      phone: ''
    });
    setErrorMessage('');
  };

  // Memoized input change to prevent unnecessary re-renders.
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleCouponCodeChange = useCallback((index, value) => {
    setFormData(prevState => {
      const newCouponCodes = [...prevState.couponCodes];
      newCouponCodes[index] = value;
      return {
        ...prevState,
        couponCodes: newCouponCodes
      };
    });
  }, []);

  const addCouponCode = useCallback(() => {
    setFormData(prevState => ({
      ...prevState,
      couponCodes: [...prevState.couponCodes, '']
    }));
  }, []);

  const removeCouponCode = useCallback((index) => {
    if (formData.couponCodes.length > 1) {
      setFormData(prevState => {
        const newCouponCodes = prevState.couponCodes.filter((_, i) => i !== index);
        return {
          ...prevState,
          couponCodes: newCouponCodes
        };
      });
    }
  }, [formData.couponCodes]);

  const validateForm = () => {
    if (!formData.username.trim() || !formData.fullName.trim()) {
      setErrorMessage('Username and Full Name are required');
      return false;
    }
    const filteredCodes = formData.couponCodes.map(code => code.trim()).filter(code => code !== '');
    if (filteredCodes.length === 0) {
      setErrorMessage('At least one coupon code is required');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleAddAgent = async () => {
    if (!validateForm()) return;
    try {
      const response = await fetch(`${baseUrl}/agents/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username.trim(),
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          couponCodes: formData.couponCodes.map(code => code.trim()).filter(code => code !== '')
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add agent');
      }
      setIsAddModalOpen(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      console.error('Error adding agent:', error);
      setErrorMessage(error.message);
    }
  };

  const handleEditAgent = async () => {
    if (!validateForm()) return;
    try {
      const response = await fetch(`${baseUrl}/agents/agents`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username.trim(),
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          couponCodes: formData.couponCodes.map(code => code.trim()).filter(code => code !== '')
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update agent');
      }
      setIsEditModalOpen(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      console.error('Error updating agent:', error);
      setErrorMessage(error.message);
    }
  };

  const handleDeleteAgent = async (username) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      try {
        const response = await fetch(`${baseUrl}/agents/agents/${username}`, { method: 'DELETE' });
        if (!response.ok) {
          throw new Error('Failed to delete agent');
        }
        fetchAgents();
      } catch (error) {
        console.error('Error deleting agent:', error);
      }
    }
  };

  const openEditModal = (agent) => {
    const couponCodes = Array.isArray(agent.couponCodes) && agent.couponCodes.length > 0
      ? agent.couponCodes
      : [''];
    setFormData({
      username: agent.username,
      fullName: agent.fullName,
      couponCodes: couponCodes,
      email: agent.email || '',
      phone: agent.phone || ''
    });
    setIsEditModalOpen(true);
    setErrorMessage('');
  };

  const renderCouponCodes = (agent) => (
    <div className="flex flex-wrap gap-1">
      {Array.isArray(agent.couponCodes) && agent.couponCodes.map((code, index) => (
        <Badge key={`badge-${agent.username}-${index}`} variant="secondary">
          {code}
        </Badge>
      ))}
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Manage Agents</CardTitle>
              <CardDescription>View and manage your agent network</CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setIsAddModalOpen(true); }}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Agent
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Full Name</TableHead>
                <TableHead>Coupon Codes</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No agents found
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => (
                  <TableRow key={agent.username}>
                    <TableCell className="font-medium">{agent.username}</TableCell>
                    <TableCell>{agent.fullName}</TableCell>
                    <TableCell>{renderCouponCodes(agent)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {agent.email && <div>{agent.email}</div>}
                        {agent.phone && <div>{agent.phone}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(agent)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteAgent(agent.username)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="text-sm text-muted-foreground mt-4">
            {agents.length} agent{agents.length !== 1 ? 's' : ''} in total
          </div>
        </CardContent>
      </Card>

      {/* Add Agent Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setIsAddModalOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Agent</DialogTitle>
          </DialogHeader>
          <FormContent
            isEdit={false}
            formData={formData}
            errorMessage={errorMessage}
            handleInputChange={handleInputChange}
            handleCouponCodeChange={handleCouponCodeChange}
            addCouponCode={addCouponCode}
            removeCouponCode={removeCouponCode}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setIsAddModalOpen(false); }}>
              Cancel
            </Button>
            <Button onClick={handleAddAgent}>Add Agent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setIsEditModalOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
          </DialogHeader>
          <FormContent
            isEdit={true}
            formData={formData}
            errorMessage={errorMessage}
            handleInputChange={handleInputChange}
            handleCouponCodeChange={handleCouponCodeChange}
            addCouponCode={addCouponCode}
            removeCouponCode={removeCouponCode}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setIsEditModalOpen(false); }}>
              Cancel
            </Button>
            <Button onClick={handleEditAgent}>Update Agent</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAgents;
